import { useState } from "react";
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  MessageCircle,
  Building2,
  HelpCircle,
  ChevronDown,
  ArrowRight,
} from "lucide-react";

const contactCards = [
  {
    Icon: MessageCircle,
    title: "Renter Support",
    description: "Need help with bookings, complaints, or refunds?",
    action: "support@aapkapg.com",
  },
  {
    Icon: Building2,
    title: "Owner Partnerships",
    description: "List your PG on AapkaPG and reach thousands of renters.",
    action: "partners@aapkapg.com",
  },
  {
    Icon: HelpCircle,
    title: "General Enquiries",
    description: "Press, media, or anything else — we'd love to hear from you.",
    action: "hello@aapkapg.com",
  },
];

const infoItems = [
  { Icon: Mail, label: "Email", value: "hello@aapkapg.com" },
  { Icon: Phone, label: "Phone", value: "+91 99999 99999" },
  { Icon: MapPin, label: "Office", value: "Pitampura 110034" },
];

const faqs = [
  {
    q: "How quickly do you respond?",
    a: "We aim to reply to all queries within 24 hours during business days. Urgent booking issues are typically resolved within 4 hours.",
  },
  {
    q: "I'm a PG owner. How do I list my property?",
    a: "Email partners@aapkapg.com or visit our homepage and click 'List Your PG'. Our team will guide you through the onboarding process and the 50-point Trust Shield audit.",
  },
  {
    q: "Is there a charge for listing?",
    a: "Basic listings are free forever. We offer premium visibility plans for owners who want to appear at the top of search results.",
  },
  {
    q: "Can I visit your office?",
    a: "Absolutely! Walk in during business hours (Mon–Sat, 9 AM – 7 PM) at our Karol Bagh office. No appointment needed.",
  },
];

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="ct-card ct-faq-card">
      <button onClick={() => setOpen(!open)} className="ct-faq-btn">
        <span className="ct-fw-600">{question}</span>
        <ChevronDown
          className={`ct-faq-chevron ${open ? "ct-faq-chevron--open" : ""}`}
        />
      </button>
      {open && <div className="ct-faq-answer ct-muted">{answer}</div>}
    </div>
  );
}

function ContactForm() {
  const [submitted, setSubmitted] = useState(false);
  if (submitted) {
    return (
      <div className="ct-form-success">
        <div className="ct-icon-box ct-icon-box--md ct-success-icon">
          <Send className="ct-icon" />
        </div>
        <h3 className="ct-fw-700 ct-success-title">Message sent!</h3>
        <p className="ct-muted ct-success-sub">
          We'll get back to you within 24 hours.
        </p>
      </div>
    );
  }
  return (
    <form
      className="ct-form"
      onSubmit={(e) => {
        e.preventDefault();
        setSubmitted(true);
      }}
    >
      <div className="ct-form-row">
        <div className="ct-form-group">
          <label>Name</label>
          <input required type="text" placeholder="Your full name" />
        </div>
        <div className="ct-form-group">
          <label>Email</label>
          <input required type="email" placeholder="you@example.com" />
        </div>
      </div>
      <div className="ct-form-group">
        <label>Subject</label>
        <select required>
          <option value="">Select a topic</option>
          <option>Renter Support</option>
          <option>Owner Partnership</option>
          <option>Press / Media</option>
          <option>Other</option>
        </select>
      </div>
      <div className="ct-form-group">
        <label>Message</label>
        <textarea required rows={5} placeholder="Tell us how we can help..." />
      </div>
      <button type="submit" className="ct-btn-primary ct-btn-full">
        <Send className="ct-send-icon" /> Send Message
      </button>
    </form>
  );
}

export default function Contact() {
  return (
    <div className="ct-root">
      {/* Hero */}
      <section className="ct-hero">
        <div className="ct-hero__bg" />
        <div className="ct-hero__glow" />
        <div className="ct-container ct-hero__content">
          <span className="ct-badge">
            <span className="ct-badge__dot" /> Get in Touch
          </span>
          <h1 className="ct-fluid-hero ct-hero__title">
            We'd love to <span className="ct-primary">hear from you</span>
          </h1>
          <p className="ct-fluid-lg ct-hero__sub">
            Whether you're a renter looking for help, a PG owner wanting to
            partner up, or just curious — our team is here for you.
          </p>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="ct-section ct-section--cards">
        <div className="ct-container ct-cards-grid">
          {contactCards.map((c) => (
            <div key={c.title} className="ct-card ct-contact-card">
              <div className="ct-icon-box ct-icon-box--md ct-card-icon">
                <c.Icon className="ct-icon" />
              </div>
              <h3 className="ct-fw-700 ct-card-title">{c.title}</h3>
              <p className="ct-muted ct-card-desc">{c.description}</p>
              <a href={`mailto:${c.action}`} className="ct-card-email">
                {c.action} <ArrowRight className="ct-arrow-icon" />
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* Form + Info */}
      <section className="ct-section ct-section--muted">
        <div className="ct-container ct-form-layout">
          <div className="ct-card ct-card--lg">
            <h2 className="ct-fluid-2xl ct-fw-800">Send us a message</h2>
            <p className="ct-muted ct-form-sub">
              Fill out the form and we'll get back to you within 24 hours.
            </p>
            <ContactForm />
          </div>
          <div className="ct-info-sidebar">
            {infoItems.map((item) => (
              <div key={item.label} className="ct-card ct-info-card">
                <div className="ct-icon-box ct-icon-box--sm">
                  <item.Icon className="ct-icon" />
                </div>
                <div>
                  <div className="ct-info-label">{item.label}</div>
                  <div className="ct-fw-600 ct-info-value">{item.value}</div>
                </div>
              </div>
            ))}
            <div className="ct-card ct-map-card">
              <iframe
                title="AapkaPG Office"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3499.7645267357366!2d77.11296057628836!3d28.69668968116279!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d03e51fdd1ac1%3A0x58fe86ba7414e846!2sVardhman%20Premium%20Mall!5e0!3m2!1sen!2sin!4v1778482875877!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: "230px" }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="ct-section">
        <div className="ct-container ct-container--narrow">
          <div className="ct-section-header">
            <span className="ct-badge">FAQ</span>
            <h2 className="ct-fluid-3xl ct-fw-800 ct-mt-md">
              Common Questions
            </h2>
          </div>
          <div className="ct-faq-list">
            {faqs.map((f) => (
              <FaqItem key={f.q} question={f.q} answer={f.a} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
